/**
 * ChangeOrderReviewSend — CO review + send-for-signature page.
 *
 * PT-35 (2026-07-28). Route: `/change-orders/:id/review`
 *
 * Consumes:
 *   - ReviewAndSendShell (PT-34): 2-column layout, recipients, toggles, footer
 *   - DocumentReviewSurface via CODocument (customer-facing renderer, TBD)
 *
 * Owns:
 *   - CO data loading via api.getChangeOrder(id)
 *   - Line-item + delta computation (via change-order engine, not quote engine)
 *   - Send action: api.sendChangeOrder(id, sendData) with sendMethod 'esign'
 *     (the only send, IDI-464): the server issues or reuses the signature
 *     token, emails the signer the link and returns the delivery outcome,
 *     which SigningLinkResult reports with the link always copyable.
 *     Acceptance off-platform is recorded via the manual attestation route
 *     (record_decision-equivalent, permissions-gated).
 *   - No CO-specific reorder/margin/notes code — everything inherited from
 *     shared primitives (35.3 asserts this).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Spinner } from '../ui/Spinner';
import SelectMenu from '../ui/SelectMenu';
import ReviewAndSendShell, { seedRecipientsFromContacts } from '../shared/ReviewAndSendShell';
import SharedSigningLinkResult, { signingLinkFor } from '../shared/SigningLinkResult';
import { dedupeEmails } from '../shared/SendComposerCc';
import SendComposerModal from '../shared/SendComposerModal';
import api from '../../services/api';
import { useQuoteAutoSave } from '../../quote-builder';
import useBranding from '../../hooks/useBranding';
import { loadTCTemplates } from '../../utils/loadTCTemplates';
import { useToast } from '../ui/Toast';
import { summarizeChangeOrder, coLines } from '../../change-order/coPrice';
import { acceptedLine } from '../shared/acceptedLine';
import { coStatusLabel, coStatusTone, coTitle, isCoDraftLike } from './coDisplay';
import ChangeOrderSignView from '../signature/ChangeOrderSignView';
import './ChangeOrderReviewSend.print.css';

// IDI-726 D1: the quote's display options and keys (QuoteReviewSend), minus
// labor pricing (a change-order line has no customer labor price). Each one
// shapes ChangeOrderSignView, as on the quote's document.
const DISPLAY_TOGGLES = [
  { id: 'groupByArea', label: 'Group by area', defaultOn: true },
  { id: 'showSqft', label: 'Show square footage', defaultOn: true },
  { id: 'lineItemPricing', label: 'Show line-item pricing', defaultOn: true },
  { id: 'showTax', label: 'Show tax', defaultOn: true },
  { id: 'notes', label: 'Include notes', defaultOn: true },
];
// IDI-726 D2: keys stored before the quote's set, read as their successors.
const LEGACY_DISPLAY_KEYS = { lineItemPricing: 'line_pricing' };

// IDI-137 (D2/D3): the job's own terms are offered as the picker's first
// option. Only terms the customer SIGNED (termsSource accepted_configuration,
// IDI-460) are called signed; the quote's template terms never are.
export const JOB_TERMS_ID = 'job_terms';
export function jobTermsLabel(termsSource) {
  return termsSource === 'accepted_configuration'
    ? 'Signed quote terms'
    : "Quote's current terms (not the signed copy)";
}
const TERMS_SAVE_ERROR = "Couldn't save the terms. Try again.";

// IDI-640 D5: the last send's display options, else the defaults (as
// QuoteReviewSend's restoreDisplayOptions).
function restoreCoDisplayOptions(co) {
  const out = Object.fromEntries(DISPLAY_TOGGLES.map((t) => [t.id, t.defaultOn]));
  const stored = co?.displayOptions;
  if (!stored || typeof stored !== 'object') return out;
  DISPLAY_TOGGLES.forEach((t) => {
    const legacy = LEGACY_DISPLAY_KEYS[t.id];
    if (typeof stored[t.id] === 'boolean') out[t.id] = stored[t.id];
    else if (legacy && typeof stored[legacy] === 'boolean') out[t.id] = stored[legacy];
  });
  return out;
}

// IDI-640 D1: one signer ticked: the last signer (recipientContactId, else
// recipientEmail), added if missing; else the primary; else the first.
function seedCoSigner(contacts, co) {
  const rows = seedRecipientsFromContacts(contacts).map((r) => ({ ...r }));
  const lastId = String(co?.recipientContactId || '');
  const lastEmail = String(co?.recipientEmail || '').trim().toLowerCase();
  let idx = lastId ? rows.findIndex((r) => r.id === lastId) : -1;
  if (idx < 0 && lastEmail) idx = rows.findIndex((r) => r.email.trim().toLowerCase() === lastEmail);
  if (idx < 0 && lastEmail) {
    rows.push({ id: lastId || `signer-${lastEmail}`, email: co.recipientEmail.trim(), name: co.recipientName || '', checked: true });
    idx = rows.length - 1;
  }
  if (idx < 0) idx = Math.max(0, rows.findIndex((r) => r.checked));
  rows.forEach((r, i) => { r.checked = i === idx; });
  return rows;
}

// IDI-640 D3/D4: the send label and the lock follow status, never sentAt.
const CO_APPROVED_STATUSES = ['accepted', 'approved', 'applied'];
function coLockState(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'sent') return 'sent';
  if (CO_APPROVED_STATUSES.includes(s)) return 'approved';
  return null;
}

function fmtCents(cents) {
  return `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ChangeOrderReviewSend() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [co, setCO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Recipients (seeded from parent job's customer contacts)
  const [recipients, setRecipients] = useState([]);
  // IDI-452 / IDI-308 / IDI-464: after send, the link and what the email did.
  const [sentLink, setSentLink] = useState(null);

  // Display toggles
  const [displayOptions, setDisplayOptions] = useState(
    Object.fromEntries(DISPLAY_TOGGLES.map((t) => [t.id, t.defaultOn]))
  );

  // IDI-726 D5: the rail's Notes are the change order's customer `notes`
  // (never internalNotes or sendMessage).
  const [railNotes, setRailNotes] = useState('');
  const [notesLoaded, setNotesLoaded] = useState(false);

  // W3.5: T&C template picker (mirrors QuoteReviewSend)
  const [tcTemplateId, setTcTemplateId] = useState('');
  // IDI-460: the parent job's signed-quote terms, used as the default when
  // the change order has no T&C selection of its own.
  const [signedQuoteTerms, setSignedQuoteTerms] = useState('');
  const [termsSource, setTermsSource] = useState('');
  const [tcTemplates, setTcTemplates] = useState([]);
  // IDI-137: the option list is settled (templates + job read) before the
  // on-open save picks a default.
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [jobLoaded, setJobLoaded] = useState(false);
  // IDI-640 D6: the on-open save needs a good job read.
  const [jobReadOk, setJobReadOk] = useState(false);
  const [termsError, setTermsError] = useState('');
  const autoSaved = useRef(false);

  // IDI-137 (D1): the preview is the customer projection the signing page
  // renders (IDI-302, U-10), re-read after each terms save.
  const [view, setView] = useState(null);
  const [viewError, setViewError] = useState(false);
  const viewSeq = useRef(0);

  // Send state
  const [sending, setSending] = useState(false);
  // IDI-640 D2: Send opens the composer; it is the confirm.
  const [composerOpen, setComposerOpen] = useState(false);
  // CC seeds (IDI-548): the job's quote salespersons.
  const [ccQuoteId, setCcQuoteId] = useState('');
  const [ccQuote, setCcQuote] = useState(null);

  // PR-36 §1.4 (2026-08-17): tenant branding via shared hook.
  const { branding } = useBranding();

  // ─── Load CO ─────────────────────────────────────────────────────────────
  // PR-26 §1: T&C templates loader with default fallback. Branding moved to
  // the useBranding hook (PR-36 §1.4, 2026-08-17); the interleaved fetch is
  // untangled here so the T&C loader can go straight to the point.
  // PR-32 (308_ §3) / IDI-137: a lone 'default' option is picked AND saved on
  // open (see the on-open save below), so COs don't ship bare.
  useEffect(() => {
    let c = false;
    loadTCTemplates().then((items) => {
      if (c) return;
      setTcTemplates(items);
      setTemplatesLoaded(true);
    });
    return () => { c = true; };
  }, []);

  const loadView = useCallback(async () => {
    const seq = ++viewSeq.current;
    try {
      const res = await api.getChangeOrderCustomerView(id);
      if (seq !== viewSeq.current) return;
      setView(res || null);
      setViewError(!res);
    } catch {
      if (seq === viewSeq.current) setViewError(true);
    }
  }, [id]);

  useEffect(() => { loadView(); }, [loadView]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.getChangeOrder(id);
        const data = res?.data || res;
        if (!cancelled && data) {
          setCO(data);
          setDisplayOptions(restoreCoDisplayOptions(data));
          setRailNotes(data.notes || '');
          setNotesLoaded(true);
          // IDI-460: read the stored T&C selection back, so a reload shows
          // what was picked rather than resetting to blank.
          if (data.tcTemplateId) setTcTemplateId(data.tcTemplateId);
          // ...and when nothing has been picked yet, default to the terms the
          // customer actually SIGNED on the parent job's quote (ruled
          // 2026-10-06). An amendment should carry the contract's terms, not
          // a blank or whatever today's template happens to be. Exposed by
          // the job read's `signedTax.termsConditions` (IDI-458's block).
          // IDI-137: read whether or not a template is stored, so the
          // option list and its label (D3) are the same either way.
          // Seed recipients from parent job's customer contacts (same read).
          const jobId = data.job_id || data.jobId;
          if (jobId) {
            try {
              const jobRes = await api.getJob(jobId);
              const job = jobRes?.data || jobRes;
              const signedTerms = job?.signedTax?.termsConditions;
              if (signedTerms && !cancelled) {
                setSignedQuoteTerms(signedTerms);
                setTermsSource(job?.signedTax?.termsSource || '');
              }
              if (!cancelled) {
                setJobReadOk(true);
                setCcQuoteId(job?.quoteId || job?.quote_id || '');
              }
              // IDI-640 D1: the job's customer's contacts (as InvoiceReviewSend);
              // a job carries no contacts of its own.
              const cid = job?.customerId || job?.customer_id;
              let contacts = [];
              if (cid) {
                try {
                  const custRes = await api.getCustomer(cid);
                  const cust = custRes?.data || custRes;
                  contacts = cust?.contacts || [];
                } catch {
                  // Non-fatal: the stored signer or "+ Add recipient" still works.
                }
              }
              const seeded = seedCoSigner(contacts, data);
              if (!cancelled && seeded.length > 0) setRecipients(seeded);
              // Fallback: no contacts and no stored signer, but the job has an email.
              if (!cancelled && seeded.length === 0 && (job?.customer_email || job?.customer?.email)) {
                const email = job.customer_email || job.customer.email;
                setRecipients([{ id: 'primary', email, name: job.customer_name || job.customer?.name || '', checked: true }]);
              }
            } catch {
              // Non-fatal: contractor can add recipients manually via the shell
              // (the stored signer is still offered).
              const seeded = seedCoSigner([], data);
              if (!cancelled && seeded.length > 0) setRecipients(seeded);
            }
          }
          if (!cancelled) setJobLoaded(true);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load change order.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // CC seeds (IDI-548): the job's quote, as InvoiceReviewSend.
  useEffect(() => {
    if (!ccQuoteId || typeof api.getQuote !== 'function') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const q = await api.getQuote(ccQuoteId);
        if (!cancelled) setCcQuote(q?.data || q || null);
      } catch { /* no seeds: the composer's CC row still works */ }
    })();
    return () => { cancelled = true; };
  }, [ccQuoteId]);

  // ─── Compute totals via change-order engine (35.3: no CO-specific margin code) ─
  // IDI-492 (C-03/C-04): this is a READ-ONLY screen, so it prices with the
  // ONE shared reader (coPrice) the server uses to apply, invoice and show
  // the customer. The net comes from the lines, never a stored netTotal.
  const computed = useMemo(() => {
    if (!co) return null;
    const lines = coLines(co);
    const s = summarizeChangeOrder(lines);
    // IDI-640 D7 (D-03): net change only; original -> revised stays on the
    // builder and detail page.
    return {
      netTotal: s.netCents,
      needsRepricing: s.needsRepricing,
      unpricedAdded: s.unpricedAdded,
    };
  }, [co]);

  // ─── T&C (IDI-137 D2) ───────────────────────────────────────────────────
  const draftLike = isCoDraftLike(co?.status);
  const termsFor = useCallback(
    (tid) => (tid === JOB_TERMS_ID ? signedQuoteTerms : tcTemplateBody(tcTemplates, tid)),
    [signedQuoteTerms, tcTemplates],
  );

  // Stored: tcTemplateId and its terms, through the PUT (read-merge-write).
  // Only a Draft saves; the preview re-reads what was stored.
  const saveTerms = useCallback(async (tid) => {
    if (!draftLike || !tid) return;
    const terms = termsFor(tid);
    setTcTemplateId(tid);
    setTermsError('');
    try {
      await api.updateChangeOrder(id, { tcTemplateId: tid, terms });
      setCO((prev) => ({ ...(prev || {}), tcTemplateId: tid, terms }));
      loadView();
    } catch {
      setTermsError(TERMS_SAVE_ERROR);
    }
  }, [draftLike, termsFor, id, loadView]);

  // On open, a Draft with no pick saves the job's terms, else a lone
  // "Default terms". Never for a Sent/Approved/Rejected change order.
  useEffect(() => {
    if (autoSaved.current || !co || !jobLoaded || !templatesLoaded) return;
    autoSaved.current = true;
    // IDI-640 D6: after a failed job read the job's terms are unknown, so
    // nothing is saved; the next open with a good read saves them.
    if (!jobReadOk) return;
    if (!draftLike || co.tcTemplateId) return;
    if (signedQuoteTerms) saveTerms(JOB_TERMS_ID);
    else if (tcTemplates.length === 1 && tcTemplates[0].id === 'default') saveTerms('default');
  }, [co, jobLoaded, jobReadOk, templatesLoaded, draftLike, signedQuoteTerms, tcTemplates, saveTerms]);

  // Not a Draft: show the option whose terms are stored, else the placeholder.
  const shownTcTemplateId = useMemo(() => {
    if (draftLike) return tcTemplateId;
    const stored = co?.terms || '';
    if (!stored) return '';
    if (signedQuoteTerms && stored === signedQuoteTerms) return JOB_TERMS_ID;
    const match = (tcTemplates || []).find((t) => t.content === stored);
    return match ? (match.id || match.name || '').toString() : '';
  }, [draftLike, tcTemplateId, co, signedQuoteTerms, tcTemplates]);

  // IDI-726 D5: the rail's Notes save as typed while unlocked (Draft, an old
  // withdrawn one, Rejected), as QuoteReviewSend's notes do: `{notes}` alone,
  // through the merging PUT. Opening writes nothing; Sent and Approved are
  // locked and never save. The T&C save stays its own PUT.
  const notesLocked = !!coLockState(co?.status);
  const notesPayload = useMemo(() => ({ notes: railNotes }), [railNotes]);
  const { error: notesSaveError, cancelPending: cancelNotesSave } = useQuoteAutoSave(id, notesPayload, {
    apiUpdate: (coId, data) => api.updateChangeOrder(coId, data),
    apiCreate: null,
    dirtyCheckKeys: ['notes'],
    useAliases: false,
    unloadPath: (coId) => `/v1/change-orders/${coId}`,
    enabled: notesLoaded && !notesLocked,
    hydrated: notesLoaded,
  });

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    const jobId = co?.job_id || co?.jobId;
    if (jobId) {
      navigate(`/jobs/${jobId}`, { state: { tab: 'change-orders' } });
    } else {
      navigate('/jobs');
    }
  }, [co, navigate]);

  // IDI-298: Download saves the server PDF of the customer view (no print
  // dialog). A Draft or Rejected posts the rail's unsaved switches and notes,
  // as previewed; Sent and Approved post nothing, so the file is what the
  // customer signs. `lockState` is declared below, so read the status here.
  // Cmd+P still prints the preview (.co-doc print-root).
  const handleDownload = useCallback(async () => {
    const locked = !!coLockState(co?.status);
    const body = locked ? {} : { displayOptions, notes: railNotes };
    try {
      const res = await api.generateChangeOrderPDF(id, body);
      const data = res?.data || res;
      if (!data?.pdf_base64) throw new Error('no pdf');
      const byteChars = atob(data.pdf_base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: data.content_type || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || `ChangeOrder_${co?.coNumber || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Couldn't generate the PDF. Please try again.");
    }
  }, [id, co, displayOptions, railNotes, toast]);

  const handleSend = useCallback(() => {
    const signer = recipients.find((r) => r.checked);
    if (!signer || !signer.email) {
      toast.error('Choose the contact who will sign this change order.');
      return;
    }
    setComposerOpen(true);
  }, [recipients, toast]);

  // IDI-800 D6: after Send's 409, a consistent re-read of the record as it
  // now stands (getChangeOrder is a ConsistentRead). Only `co` and the view:
  // the load effect would re-seed the recipients and re-read the job. If the
  // record is now locked, its stored Notes and switches show and a pending
  // Notes save is dropped; unlocked, the typed ones stay. No PUT follows.
  const refreshAfterRefusal = useCallback(async () => {
    autoSaved.current = true;
    let fresh = null;
    try {
      const res = await api.getChangeOrder(id);
      fresh = res?.data || res || null;
    } catch {
      // The read failed: the job's change orders carry the record too.
      const jobId = co?.job_id || co?.jobId;
      if (jobId) {
        try {
          const res = await api.getChangeOrders(jobId);
          const items = Array.isArray(res) ? res : (res?.data || res?.change_orders || []);
          fresh = (Array.isArray(items) ? items : []).find((c) => c && c.id === id) || null;
        } catch {
          fresh = null;
        }
      }
    }
    if (!fresh) return;
    setCO(fresh);
    if (coLockState(fresh.status)) {
      cancelNotesSave();
      setRailNotes(fresh.notes || '');
      setDisplayOptions(restoreCoDisplayOptions(fresh));
    }
    await loadView();
  }, [id, co, cancelNotesSave, loadView]);

  const doSend = useCallback(async (composer = {}) => {
    const signer = recipients.find((r) => r.checked);
    if (!signer || !signer.email) {
      toast.error('Choose the contact who will sign this change order.');
      return;
    }
    try {
      setSending(true);
      // IDI-452 / IDI-464: the backend (cmd/changeorders sendChangeOrder) reads
      // recipientEmail + sendMethod 'esign' (the only send), mints or reuses the
      // signing token and emails the signer the link.
      const payload = buildChangeOrderSendPayload(signer, {
        displayOptions,
        notes: railNotes,
        message: composer.message,
        subject: composer.subject,
        cc: composer.cc,
        tcTemplateId,
        terms: tcTemplateBody(tcTemplates, tcTemplateId) || signedQuoteTerms,
      });
      const res = await api.sendChangeOrder(id, payload);
      const saved = res?.data || res || {};
      const token = saved.signatureToken || saved.signature_token;
      if (!token) {
        toast.error('The change order was not given a signing link. Please try again.');
        return;
      }
      // IDI-726 D5: the send stored the notes; drop the pending notes save.
      cancelNotesSave();
      setComposerOpen(false);
      setCO((prev) => ({ ...(prev || {}), ...saved }));
      // IDI-464: show what the email actually did (IDI-92: only "sent" claims
      // it went out). The link stays copyable whatever happened.
      setSentLink({
        url: saved.signing_link || signingLinkFor(token),
        email: signer.email,
        deliveryStatus: saved.delivery_status,
        deliveryDetail: saved.delivery_detail,
        sentTo: saved.sent_to,
        expiresAt: saved.signatureExpiresAt,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // IDI-800 D6: the server's sentence (on needs_repricing `error` is
        // the code, so read `message`), then the record as it stands.
        const msg = err?.response?.data?.message || 'This change order changed while you were sending it. Reload and try again.';
        toast.error(`Failed to send: ${msg}`);
        setComposerOpen(false);
        await refreshAfterRefusal();
      } else if (status === 404) {
        toast.error('Send endpoint not available yet — API pending.');
      } else {
        toast.error('Failed to send change order. Please try again.');
      }
    } finally {
      setSending(false);
    }
  }, [id, recipients, displayOptions, railNotes, tcTemplateId, tcTemplates, signedQuoteTerms, toast, cancelNotesSave, refreshAfterRefusal]);

  // Recipient handlers
  // IDI-452 / IDI-344: a change order has ONE signer. Picking a contact
  // selects it and clears the others, so the e-sign recipient is unambiguous.
  const handleToggleRecipient = useCallback((rid) => {
    setRecipients((prev) => prev.map((r) => ({ ...r, checked: r.id === rid || r.email === rid ? !r.checked : false })));
  }, []);

  const handleAddRecipient = useCallback((email) => {
    setRecipients((prev) => [
      ...prev.map((r) => ({ ...r, checked: false })),
      { id: `added-${Date.now()}`, email, name: '', checked: true },
    ]);
  }, []);

  const handleToggleDisplay = useCallback((key) => {
    setDisplayOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    );
  }

  if (error && !co) {
    return (
      <Alert variant="error" className="m-6">{error}</Alert>
    );
  }

  // IDI-492 (C-04): an added line with no price can't be sent (the server
  // refuses it too, with a 409).
  const canSend = recipients.filter((r) => r.checked).length === 1 && !computed?.needsRepricing;
  const signer = recipients.find((r) => r.checked);
  const signerEmail = signer?.email || '';
  const lockState = coLockState(co?.status);

  if (sentLink) {
    const jobId = co?.job_id || co?.jobId;
    return (
      <SigningLinkResult
        link={sentLink}
        onDone={() => navigate(jobId ? `/jobs/${jobId}` : '/jobs', { state: { tab: 'change-orders' } })}
      />
    );
  }

  return (
    <>
    {/* IDI-726 D5: a failed notes save shows as the quote's does. */}
    {notesSaveError && (
      <div className="px-6 pt-4">
        <Alert variant="error">{notesSaveError}</Alert>
      </div>
    )}
    <ReviewAndSendShell
      title={coTitle(co)}
      subtitle={co?.customer_name || co?.job_title || ''}
      backTo={co?.job_id || co?.jobId ? `/jobs/${co.job_id || co.jobId}` : '/jobs'}
      backLabel="Back to job"
      status={co?.status}
      statusLabel={coStatusLabel(co?.status)}
      statusTone={coStatusTone(co?.status)}
      recipients={recipients}
      onToggleRecipient={handleToggleRecipient}
      onAddRecipient={handleAddRecipient}
      displayToggles={DISPLAY_TOGGLES}
      displayOptions={displayOptions}
      onToggleDisplay={handleToggleDisplay}
      notes={railNotes}
      onNotesChange={setRailNotes}
      onCancel={handleCancel}
      onDownload={handleDownload}
      // IDI-640 D4: locked as a sent quote. Sent can be resent (the stored
      // signer and toggles); Approved can't be sent again.
      locked={!!lockState}
      lockedLabel={lockState === 'sent' ? 'Sent \u2014 locked' : lockState === 'approved' ? 'Approved \u2014 locked' : undefined}
      lockedMessage={
        lockState === 'sent'
          ? '\u2014 this is the change order the customer was sent.'
          : lockState === 'approved' ? '\u2014 the customer accepted this change order.' : undefined
      }
      onSend={lockState === 'approved' ? undefined : handleSend}
      sendBlockedReason={lockState === 'approved' ? "Approved \u2014 it can't be sent again." : undefined}
      sending={sending}
      sendDisabled={!canSend}
      sendLabel={lockState === 'sent' ? 'Resend for signature' : 'Send for signature'}
      deliveryNote={`The signer will be emailed a link to review and sign this change order${signerEmail ? ` (${signerEmail})` : ''}. You'll also get the link to copy after sending.`}
      extraRailCards={
        <>
          {/* W3.5: T&C Template picker */}
          <Card className="p-4 space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
              Terms & Conditions
            </h4>
            <SelectMenu
              value={shownTcTemplateId}
              onChange={saveTerms}
              disabled={!draftLike}
              width="full"
              placeholder="Select T&C template…"
              options={[
                ...(signedQuoteTerms ? [{ value: JOB_TERMS_ID, label: jobTermsLabel(termsSource) }] : []),
                ...(tcTemplates.length > 0
                  ? tcTemplates.map((t) => ({
                      value: (t.id || t.name || '').toString(),
                      label: t.name || t.label || t.title || 'Untitled template',
                    }))
                  : [{ value: 'standard', label: 'Standard Terms' }]),
              ]}
            />
            {termsError && <Alert variant="error">{termsError}</Alert>}
          </Card>
          {computed?.needsRepricing && (
            <Alert variant="warning">
              Needs re-pricing: {computed.unpricedAdded} added line{computed.unpricedAdded === 1 ? ' has' : 's have'} no
              price. Open the change order in the builder and save it to price {computed.unpricedAdded === 1 ? 'it' : 'them'} before sending.
            </Alert>
          )}
          {/* Delta summary */}
          {computed && (
            <Card className="p-4">
            <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Delta summary
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Net change</span>
                <span className="tabular-nums font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  {fmtCents(computed.netTotal)}
                </span>
              </div>
            </div>
          </Card>
          )}
        </>
      }
    >
      {/* IDI-137 (D1): the preview is what the customer signs: the signing
          page's change-order view over the IDI-302 projection. */}
      <div className="co-doc print-root p-6 space-y-4">
        {/* W3.5: Tenant branding header (BR-1 consumer #7) */}
        {(branding.logo_url || branding.primary_color) && (
          <div className="flex items-center gap-3 mb-4 pb-3 border-b" style={{ borderColor: branding.primary_color || 'var(--color-border)' }}>
            {branding.logo_url && (
              <img src={branding.logo_url} alt="Company logo" className="h-8 w-auto" />
            )}
            {branding.primary_color && (
              <div className="h-1 flex-1 rounded" style={{ backgroundColor: branding.primary_color }} />
            )}
          </div>
        )}
        {viewError ? (
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }} data-testid="co-preview-unavailable">
            Preview unavailable.
          </p>
        ) : (
          <ChangeOrderSignView doc={view} options={displayOptions} notes={railNotes} />
        )}
        <COAcceptedLine co={co} />
      </div>
    </ReviewAndSendShell>
    {/* IDI-640 D2: the shared send composer is the confirm. */}
    <SendComposerModal
      open={composerOpen}
      onClose={() => setComposerOpen(false)}
      onConfirm={(composer) => doSend(composer)}
      title="Send change order"
      defaultSubject={`Please review and sign: ${coTitle(co)}`}
      initial={{ message: co?.sendMessage || '' }}
      recipientsLabel={signer?.name || signerEmail}
      recipientCount={signer ? 1 : 0}
      ccHint="CC'd people get a copy with the change order PDF, without the signing link."
      footnote="The change order PDF is attached."
      salespersons={ccQuote?.salespersons}
      salespersonIds={ccQuote?.salesperson_ids}
      assigneeEmail={ccQuote?.assigneeEmail}
      sending={sending}
    />
    </>
  );
}

// IDI-452: the send body the changeorders Lambda actually reads.
// IDI-464 / IDI-640 D2: send_composer carries only the composer's non-empty
// subject, message and cc (CC'd people get one copy with no link). An unedited
// subject goes out blank, so the server's default subject stays. The
// top-level `message` is the composer's, stored as sendMessage (the next
// send's pre-fill). The server never stores cc or send_composer.
export function buildChangeOrderSendPayload(signer, { displayOptions, notes, message, subject, cc, tcTemplateId, terms } = {}) {
  const ccList = dedupeEmails(cc || []);
  const msg = String(message || '').trim();
  const subj = String(subject || '').trim();
  const composer = {
    ...(subj ? { subject: subj } : {}),
    ...(msg ? { message: msg } : {}),
    ...(ccList.length > 0 ? { cc: ccList } : {}),
  };
  return {
    ...(Object.keys(composer).length > 0 ? { send_composer: composer } : {}),
    recipientEmail: signer.email,
    recipientName: signer.name || '',
    recipientContactId: signer.id,
    sendMethod: 'esign',
    message: msg,
    display_options: displayOptions || {},
    // IDI-460: the picked T&C template and the rail notes are PERSISTED, not
    // just used for this one request, so a reload keeps them. IDI-726 D5:
    // the rail notes are the change order's `notes`.
    tcTemplateId: tcTemplateId || '',
    terms: terms || '',
    notes: notes || '',
  };
}

// IDI-460: the selected template's text, so the terms the customer sees are
// stored on the change order rather than resolved again at render time.
export function tcTemplateBody(templates, templateId) {
  if (!templateId) return '';
  const match = (templates || []).find((t) => (t.id || t.name) === templateId);
  return match?.content || '';
}

// IDI-308: the link view moved to components/shared, used by the quote too.
// Re-exported so existing imports keep working.
export { signingLinkFor };

// IDI-308 / IDI-464: the change order's link view is the shared one, reporting
// the server's delivery outcome (sent / redirected / failed …).
export function SigningLinkResult({ link, onDone }) {
  return (
    <SharedSigningLinkResult
      url={link.url}
      documentType="change_order"
      deliveryStatus={link.deliveryStatus}
      deliveryDetail={link.deliveryDetail}
      sentTo={link.sentTo}
      recipient={link.email}
      expiresAt={link.expiresAt}
      doneLabel="Back to job"
      onDone={onDone}
    />
  );
}

// IDI-547: an accepted CO's read-only Review & Send shows the same two-state
// line as quotes — "E-signed [by X] on … at …" or "Accepted manually on … at …".
// Never a channel name. Draft/sent COs show nothing.
const ACCEPTED_STATUSES = ['accepted', 'approved', 'applied'];
export function COAcceptedLine({ co }) {
  if (!ACCEPTED_STATUSES.includes(String(co?.status || '').toLowerCase())) return null;
  return (
    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }} data-testid="co-accepted-line">
      {acceptedLine(co)}
    </p>
  );
}
